import { useEffect, useState } from "react";
import { freshness } from "@/lib/swapCalc";

export const BrokerMark = ({ broker, size = 40, className = "" }) => (
  <img
    src={broker.logo}
    alt={broker.name}
    width={size}
    height={size}
    style={{ width: size, height: size }}
    className={`shrink-0 select-none object-contain ${className}`}
    draggable={false}
    data-testid={`broker-mark-${broker.slug}`}
  />
);

export const MatchRing = ({ value, size = 64, stroke = 4, label = true, animate = true }) => {
  const [v, setV] = useState(animate ? 0 : value);
  useEffect(() => { const t = setTimeout(() => setV(value), 60); return () => clearTimeout(t); }, [value]);
  const r = (size - stroke) / 2, c = 2 * Math.PI * r;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }} data-testid="match-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="ring-track" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="ring-fill" strokeDasharray={c} strokeDashoffset={c - (c * v) / 100} />
      </svg>
      {label && (
        <span className="absolute font-mono font-normal tabular text-[var(--ink)]" style={{ fontSize: size * 0.28 }}>
          {value}<span className="text-[0.6em] text-[var(--ink-3)]">%</span>
        </span>
      )}
    </div>
  );
};

export const Freshness = ({ source, className = "" }) => {
  const f = freshness(source) || { label: "Unknown", tone: "na" };
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.01em] tone-${f.tone} ${className}`} data-testid="freshness-badge">
      <span className="status-dot" />{f.label}
    </span>
  );
};

const STATUS_TONE = {
  Active: "bg-[var(--cobalt-soft)] text-[var(--cobalt)]",
  Pending: "bg-[#fffbeb] text-[var(--warn)]",
  "Target reached": "bg-[#ecfdf5] text-[var(--pos)]",
  Closed: "bg-[var(--canvas-cool)] text-[var(--ink-2)]",
  Stopped: "bg-[#fff1f2] text-[var(--neg)]",
};

export const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 h-6 font-mono text-[11px] tracking-[0.01em] ${STATUS_TONE[status]}`} data-testid="status-badge">
    {status}
  </span>
);

export const Direction = ({ value }) => (
  <span className={`inline-flex items-center gap-1 font-mono text-xs tracking-[0.01em] ${value === "Long" ? "text-[var(--pos)]" : "text-[var(--neg)]"}`}>
    <span className={`inline-block h-0 w-0 border-x-[4px] border-x-transparent ${value === "Long" ? "border-b-[6px] border-b-current" : "border-t-[6px] border-t-current"}`} />
    {value}
  </span>
);

export const Tag = ({ children, tone = "default" }) => (
  <span className={`inline-flex items-center rounded-md px-2 h-6 text-[12px] ${tone === "blue" ? "bg-[var(--cobalt-soft)] text-[var(--cobalt)]" : "bg-[var(--canvas-cool)] text-[var(--ink-2)]"}`}>{children}</span>
);

export const Eyebrow = ({ children, className = "" }) => <p className={`eyebrow ${className}`}>{children}</p>;

export const SectionHead = ({ eyebrow, title, lead, action, align = "left" }) => (
  <div className={`flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between ${align === "center" ? "text-center items-center" : ""}`}>
    <div className="max-w-2xl">
      {eyebrow && <Eyebrow className="mb-4">{eyebrow}</Eyebrow>}
      <h2 className="t-h2">{title}</h2>
      {lead && <p className="mt-5 t-lead">{lead}</p>}
    </div>
    {action}
  </div>
);
